import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { NumCell } from "@/components/estimation/cells";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const LOC_CLS = {
  onshore: "bg-blue-50 text-[#002FA7] border-[#002FA7]/30",
  offshore: "bg-emerald-50 text-emerald-700 border-emerald-300",
};

export default function MasterRateCardTab() {
  const [roles, setRoles] = useState(null);
  const [newName, setNewName] = useState("");
  const [newOnsite, setNewOnsite] = useState(120);
  const [newOffshore, setNewOffshore] = useState(50);
  const [newLoc, setNewLoc] = useState("offshore");

  const load = () =>
    api.get("/admin/master-roles")
      .then((r) => setRoles(r.data))
      .catch((e) => toast.error(apiError(e)));

  useEffect(() => { load(); }, []);

  const update = async (role, patch) => {
    try {
      const { data } = await api.put(`/admin/master-roles/${role.id}`, { ...role, ...patch });
      setRoles((prev) => prev.map((r) => (r.id === role.id ? { ...r, ...data } : r)));
    } catch (e) { toast.error(apiError(e)); }
  };

  const create = async () => {
    const name = newName.trim();
    if (!name) return;
    if ((roles || []).some((r) => r.name.toLowerCase() === name.toLowerCase())) {
      toast.error("Role already exists in the master card");
      return;
    }
    try {
      await api.post("/admin/master-roles", {
        name, onsite_rate: Number(newOnsite) || 0, offshore_rate: Number(newOffshore) || 0,
        default_location: newLoc, position: (roles || []).length,
      });
      setNewName(""); setNewOnsite(120); setNewOffshore(50); setNewLoc("offshore");
      load();
      toast.success(`Added "${name}" to Master Rate Card`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (role) => {
    if (!window.confirm(
      `Remove "${role.name}" from the Master Rate Card?\n\nExisting opportunities that already snapshot this role will keep it — only NEW opportunities will be affected.`)) return;
    try {
      await api.delete(`/admin/master-roles/${role.id}`);
      setRoles((prev) => prev.filter((r) => r.id !== role.id));
      toast.success(`Removed "${role.name}"`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const th = "bg-zinc-50 border-b border-zinc-300 px-3 py-2 text-[10px] uppercase tracking-wide text-zinc-500 font-medium";

  if (roles === null) return <p className="text-sm text-zinc-500">Loading…</p>;

  return (
    <div data-testid="master-ratecard-tab">
      <div className="mb-6">
        <h2 className="font-heading text-base font-medium tracking-tight">Master Rate Card</h2>
        <p className="text-xs text-zinc-500 mt-1 max-w-2xl">
          Global defaults for roles, onsite/offshore rates and the preferred delivery location.
          New opportunities snapshot these values at creation time. Existing opportunities can pull updates via
          <span className="font-mono mx-1 px-1.5 py-0.5 bg-zinc-100 rounded-sm">Sync from master</span>
          on their Rate Card tab.
        </p>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm">
        <table className="w-full">
          <thead>
            <tr>
              <th className={`${th} text-left`}>Role</th>
              <th className={`${th} text-right`}>Onsite $/hr</th>
              <th className={`${th} text-right`}>Offshore $/hr</th>
              <th className={`${th} text-center`}>Default location</th>
              <th className={th}></th>
            </tr>
          </thead>
          <tbody>
            {roles.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-xs text-zinc-400">No roles in the master rate card yet.</td></tr>
            ) : roles.map((r) => {
              const loc = (r.default_location || "offshore").toLowerCase();
              return (
                <tr key={r.id} className="hover:bg-zinc-50">
                  <td className="border-b border-zinc-100 px-3 py-1.5 text-[13px]" data-testid={`master-role-name-${r.name}`}>{r.name}</td>
                  <td className="border-b border-zinc-100 w-28">
                    <NumCell value={r.onsite_rate} onCommit={(v) => update(r, { onsite_rate: v || 0 })} testId={`master-onsite-${r.name}`} />
                  </td>
                  <td className="border-b border-zinc-100 w-28">
                    <NumCell value={r.offshore_rate} onCommit={(v) => update(r, { offshore_rate: v || 0 })} testId={`master-offshore-${r.name}`} />
                  </td>
                  <td className="border-b border-zinc-100 px-2 text-center w-32">
                    <select
                      data-testid={`master-loc-${r.name}`}
                      value={loc}
                      onChange={(e) => update(r, { default_location: e.target.value })}
                      className={`text-[11px] font-medium uppercase tracking-wide rounded-sm border px-2 py-0.5 outline-none focus:ring-1 focus:ring-[#002FA7] ${LOC_CLS[loc]}`}>
                      <option value="onshore">Onshore</option>
                      <option value="offshore">Offshore</option>
                    </select>
                  </td>
                  <td className="border-b border-zinc-100 px-2 text-center w-10">
                    <button data-testid={`master-remove-${r.name}`} onClick={() => remove(r)} className="text-zinc-300 hover:text-red-600 p-1">
                      <Trash2 size={12} strokeWidth={1.5} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="flex items-center gap-2 px-4 py-3 border-t border-zinc-100 flex-wrap">
          <input
            data-testid="master-new-name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && create()}
            placeholder="Role name…"
            className="border border-zinc-300 rounded-sm px-3 py-1.5 text-xs w-56 outline-none focus:border-[#002FA7]" />
          <label className="text-[10px] uppercase tracking-wider text-zinc-500 ml-2">Onsite</label>
          <input
            data-testid="master-new-onsite"
            type="number" min={0} value={newOnsite}
            onChange={(e) => setNewOnsite(e.target.value)}
            className="border border-zinc-300 rounded-sm px-2 py-1.5 text-xs w-20 font-mono text-right outline-none focus:border-[#002FA7]" />
          <label className="text-[10px] uppercase tracking-wider text-zinc-500">Offshore</label>
          <input
            data-testid="master-new-offshore"
            type="number" min={0} value={newOffshore}
            onChange={(e) => setNewOffshore(e.target.value)}
            className="border border-zinc-300 rounded-sm px-2 py-1.5 text-xs w-20 font-mono text-right outline-none focus:border-[#002FA7]" />
          <select
            data-testid="master-new-loc"
            value={newLoc}
            onChange={(e) => setNewLoc(e.target.value)}
            className="border border-zinc-300 rounded-sm px-2 py-1.5 text-xs outline-none focus:border-[#002FA7]">
            <option value="onshore">Onshore default</option>
            <option value="offshore">Offshore default</option>
          </select>
          <button
            data-testid="master-add-role"
            onClick={create}
            className="flex items-center gap-1 text-xs text-white bg-[#002FA7] hover:bg-[#002280] rounded-sm px-3 py-1.5">
            <Plus size={12} /> Add role
          </button>
        </div>
      </div>
    </div>
  );
}
